import { App as CapacitorApp } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import type { PluginListenerHandle } from '@capacitor/core';
import { Capacitor } from '@capacitor/core';

import { toast } from '@/utils/toast';

export async function copyPaymentLink(url: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(url);
    toast.success('Payment link copied');
  } catch {
    toast.error('Couldn’t copy the link. Please try again.');
  }
}

// Path Stripe redirects to after checkout. On native it must be a universal link
// the OS hands back to the app (register in AASA + assetlinks.json); the listener
// below catches it before deep-links.ts routes it into the SPA.
export const PAYMENT_RETURN_PATH = '/payment/return';

// Where we tell Stripe to send the user back to.
//  - native: a page served by the public web app. Defaults to VITE_API_URL (prod);
//    set VITE_PAYMENT_RETURN_BASE to a tunnel/LAN URL to test against a local build.
//  - web: the current page, so we land back where the modal was
export const paymentReturnUrl = (): string => {
  if (!Capacitor.isNativePlatform()) return window.location.href;
  const base = import.meta.env.VITE_PAYMENT_RETURN_BASE || import.meta.env.VITE_API_URL;
  return `${base}${PAYMENT_RETURN_PATH}`;
};

// Open the Stripe checkout link. Web opens it in the same tab (Stripe's success_url
// brings the user back). Native opens the in-app browser and closes it on return,
// mirroring the OAuth flow in AuthForm. onReturn fires on a successful native return.
export async function openPaymentCheckout(
  checkoutUrl: string,
  onReturn?: () => void,
): Promise<void> {
  if (!Capacitor.isNativePlatform()) {
    window.location.assign(checkoutUrl);
    return;
  }

  let settled = false;
  const listeners: PluginListenerHandle[] = [];
  const cleanup = () => listeners.forEach((l) => l.remove());

  // Android app-links can hand the return URL back to the app from inside the browser —
  // close it and return. iOS never fires this from within the in-app browser, so it
  // falls through to browserFinished below.
  listeners.push(
    await CapacitorApp.addListener('appUrlOpen', async ({ url }) => {
      if (settled || !url.includes(PAYMENT_RETURN_PATH)) return;
      settled = true;
      cleanup();
      await Browser.close();
      onReturn?.();
    }),
  );

  // The reliable signal on iOS: the user closing the in-app browser (after paying or
  // cancelling — Stripe already created the incoming row when the link was made, so
  // treating both as "return" is fine). Refresh and drop the listeners.
  listeners.push(
    await Browser.addListener('browserFinished', () => {
      if (settled) return;
      settled = true;
      cleanup();
      onReturn?.();
    }),
  );

  await Browser.open({ url: checkoutUrl });
}
